/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, Lock as LockIcon } from '@mui/icons-material';
import { MaterialReactTable, useMaterialReactTable, type MRT_Cell, type MRT_ColumnDef, type MRT_TableInstance } from 'material-react-table';
import { Button } from 'components/index';
import { useMagicReport } from '../context/MagicReportContext';
import { FieldType, MagicReportState, RecapApplicationDTO } from '../magic-report.type';
import { usePrompt } from '../../../AppPrompt';
import DynamicDatePicker from '../../../common/DynamicDatePicker';
import WhiteFab from '../../../common/WhiteFab';
import { useMRTStyling } from '../../../../hooks/useMRTStyling';
import { useExcelEditing, excelSelectedCellSx } from '../../../../hooks/useExcelEditing';
import { formatCurrency, formatISODate } from '../../../../common/utils/common.util';
import { LienWaiverHistoryEntry, parseLienWaiverHistory, sortLienWaiverHistory } from './lien-waivers-draws.util';
import { filterVisibleCoLienDraws, resolveHiddenCoLienDrawIds } from './co-lien-visibility.util';
import { RECAP_OF_PREVIOUS_REPORTS_SUBSTEP } from './section-utils';

/**
 * "Recap of Previous Reports" editor at the bottom of Document Review (collapsed by default).
 *
 * - Previous Applications: the project's recap rows (`reportState.recapApplications`), one table per
 *   draw as printed. They live on the draws, not the report, so edits carry over to every later month.
 *   Saved on their own endpoint as part of each (auto)save.
 * - Previous Lien Waivers: the Lien Waivers section's `LienWaiverHistory`, saved with the sections.
 *   A covered draw's current-period row is rewritten by the Lien Waivers section, so it is read-only here.
 *
 * Tables follow ProjectPreviousFundingsTable (the Previous Fundings section): shared MRT styling,
 * click-to-edit cells, and a pinned delete action.
 */

type RecapColumnType = 'text' | 'date' | 'currency' | 'percent' | 'status';

interface RecapColumn {
  key: string;
  header: string;
  type: RecapColumnType;
  size: number;
}

const APPLICATION_COLUMNS: RecapColumn[] = [
  { key: 'applicationNumber', header: 'Appl. #', type: 'text', size: 90 },
  { key: 'applicationDate', header: 'Date', type: 'date', size: 130 },
  { key: 'requestedAmount', header: 'Requested', type: 'currency', size: 140 },
  { key: 'approvedAmount', header: 'Approved', type: 'currency', size: 140 },
  { key: 'retainage', header: 'Retainage', type: 'percent', size: 100 },
  { key: 'percentRequested', header: '% Requested', type: 'percent', size: 110 },
  { key: 'percentComplete', header: '% Complete', type: 'percent', size: 110 },
  { key: 'contractAmount', header: 'Contract Amount', type: 'currency', size: 150 },
];

const LIEN_COLUMNS: RecapColumn[] = [
  { key: 'contractor', header: 'Contractor', type: 'text', size: 220 },
  { key: 'applicationDate', header: 'Through Date', type: 'date', size: 130 },
  { key: 'applicationNumber', header: 'Application #', type: 'text', size: 140 },
  { key: 'status', header: 'Status', type: 'status', size: 140 },
  { key: 'amount', header: 'Amount', type: 'text', size: 140 },
];

const toNumberOrNull = (raw: string): number | null => {
  const num = parseFloat(String(raw).replace(/[$,%\s]/g, ''));
  return Number.isNaN(num) ? null : num;
};

const formatCellValue = (type: RecapColumnType, value: any): string => {
  if (value === null || value === undefined || value === '') return '--';
  if (type === 'currency') return formatCurrency(String(value), true, false, true);
  if (type === 'percent') return `${Number(Number(value).toFixed(2))}%`;
  if (type === 'date') return formatISODate(String(value)) || '--';
  return String(value);
};

interface RecapTableProps<T extends Record<string, any>> {
  rows: T[];
  columns: RecapColumn[];
  getRowId: (row: T) => string;
  onCellChange: (rowId: string, key: string, value: any) => void;
  onDeleteRow: (rowId: string) => void;
  isRowLocked?: (row: T) => boolean;
  lockedTooltip?: string;
}

function RecapTable<T extends Record<string, any>>({
  rows,
  columns,
  getRowId,
  onCellChange,
  onDeleteRow,
  isRowLocked,
  lockedTooltip,
}: RecapTableProps<T>) {
  const { commonMRTStyling, getActionsColumnStyling } = useMRTStyling<T>();
  const actionsColumnStyling = getActionsColumnStyling();
  const locked = useCallback((row: T) => Boolean(isRowLocked?.(row)), [isRowLocked]);

  // Excel-like editing, as in ProjectTable: click selects a cell, arrows move, typing or
  // double-click edits, Enter/arrows commit and move on.
  const tableRef = useRef<MRT_TableInstance<T> | null>(null);
  const handleSaveCell = useCallback(
    (cell: MRT_Cell<T>, value: any) => {
      const col = columns.find(c => c.key === cell.column.id);
      if (!col) return;
      const isNumeric = col.type === 'currency' || col.type === 'percent';
      onCellChange(getRowId(cell.row.original), col.key, isNumeric ? toNumberOrNull(value) : String(value ?? '').trim());
    },
    [columns, getRowId, onCellChange],
  );
  const excel = useExcelEditing<T>({
    tableInstanceRef: tableRef,
    onSaveCell: handleSaveCell,
    excludedColumnIds: ['mrt-row-actions'],
  });

  const mrtColumns = useMemo<MRT_ColumnDef<T>[]>(
    () =>
      columns.map((col): MRT_ColumnDef<T> => {
        const isNumeric = col.type === 'currency' || col.type === 'percent';
        const column: MRT_ColumnDef<T> = {
          accessorKey: col.key as any,
          header: col.header,
          size: col.size,
          Cell: ({ cell }) => formatCellValue(col.type, cell.getValue()),
          muiTableBodyCellProps: ({ cell, row }) => ({
            ...excel.getBodyCellProps({ cell }),
            sx: {
              border: 'none',
              textAlign: isNumeric ? 'right' : 'left',
              background: `${row.index % 2 === 0 ? '#f9f9f9' : '#ffffff'} !important`,
            },
          }),
        };

        if (col.type === 'date') {
          column.Edit = function RecapDateEdit({ cell, row }) {
            return (
              <Box sx={{ width: '100%', minWidth: '150px' }}>
                <DynamicDatePicker
                  fieldName={col.key}
                  value={cell.getValue<string | null>() || null}
                  onChange={(_, next) => onCellChange(getRowId(row.original), col.key, next)}
                />
              </Box>
            );
          };
        } else if (col.type === 'status') {
          // Only two statuses, so a click flips it instead of opening an editor.
          column.enableEditing = false;
          column.Cell = function RecapStatusToggle({ cell, row }) {
            const value = cell.getValue<string>();
            if (locked(row.original)) return value || '--';
            const next = value === 'Conditional' ? 'Unconditional' : 'Conditional';
            return (
              <Tooltip title={`Click to mark ${next}`} arrow={false}>
                <Chip
                  size='small'
                  label={value || 'Conditional'}
                  color={value === 'Unconditional' ? 'primary' : 'default'}
                  variant={value === 'Unconditional' ? 'filled' : 'outlined'}
                  onClick={() => onCellChange(getRowId(row.original), col.key, next)}
                  sx={{ minWidth: 110, cursor: 'pointer' }}
                />
              </Tooltip>
            );
          };
        } else {
          // The hook's blur/Enter/arrow handlers commit through handleSaveCell.
          column.muiEditTextFieldProps = ({ cell }) => ({
            type: isNumeric ? 'number' : 'text',
            inputProps: isNumeric ? { step: 0.01 } : undefined,
            ...excel.getEditTextFieldProps({ cell }),
          });
        }
        return column;
      }),
    [columns, getRowId, onCellChange, excel, locked],
  );

  const table = useMaterialReactTable({
    ...commonMRTStyling,
    columns: mrtColumns,
    data: rows,
    getRowId,
    layoutMode: 'grid',
    enableColumnFilters: false,
    enableColumnActions: false,
    enableSorting: false,
    enableDensityToggle: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableEditing: row => !locked(row.original),
    editDisplayMode: 'cell',
    initialState: { density: 'compact', columnPinning: { right: ['mrt-row-actions'] } },
    enableColumnPinning: true,
    muiTableContainerProps: {
      ...excel.getContainerProps(),
      sx: { ...((commonMRTStyling.muiTableContainerProps as any)?.sx ?? {}), ...excelSelectedCellSx, outline: 'none' },
    },
    renderRowActions: ({ row }) => (
      <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
        {locked(row.original) ? (
          <Tooltip title={lockedTooltip || ''} arrow={false}>
            <LockIcon fontSize='small' sx={{ color: 'grey.500' }} />
          </Tooltip>
        ) : (
          <Tooltip title='Delete entry' arrow={false}>
            <span>
              <WhiteFab
                size='small'
                sx={{ width: 32, height: 32, minHeight: 32 }}
                icon={<DeleteIcon fontSize='small' />}
                textColor='error'
                onClick={() => onDeleteRow(getRowId(row.original))}
              />
            </span>
          </Tooltip>
        )}
      </Stack>
    ),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 60,
        ...actionsColumnStyling,
        muiTableHeadCellProps: {
          sx: (theme: any) => ({
            ...((actionsColumnStyling.muiTableHeadCellProps as any)?.sx ?? {}),
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff',
          }),
        },
        Header: () => null,
        muiTableBodyCellProps: ({ row }) => ({
          sx: { border: 'none', background: `${row.index % 2 === 0 ? '#f9f9f9' : '#ffffff'} !important` },
        }),
      },
    },
  });

  tableRef.current = table;

  return <MaterialReactTable table={table} />;
}

/** Sub-heading with a round add button, as in the Previous Fundings section. */
const TableHeader: React.FC<{ title: string; addTooltip: string; onAdd: () => void }> = ({ title, addTooltip, onAdd }) => (
  <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ mb: 0.5 }}>
    <Typography variant='body2' color='text.secondary' fontWeight={500} sx={{ letterSpacing: 1 }}>
      {title}
    </Typography>
    <Tooltip title={addTooltip} arrow={false}>
      <Box>
        <WhiteFab size='small' icon={<AddIcon fontSize='small' />} onClick={onAdd} textColor='primary' />
      </Box>
    </Tooltip>
  </Box>
);

const newId = (): string => crypto.randomUUID();

/** Same fallback the backend uses for a draw with no recap table yet. */
const buildApplicationHeading = (contractor: string | undefined, drawName: string): string =>
  contractor?.trim() ? `${contractor.trim()}'s ${drawName} Application:` : `${drawName} Application:`;

export const RecapOfPreviousReportsSection: React.FC = () => {
  const { reportState, setReportState, setIsDirty } = useMagicReport();
  const { getConfirmation } = usePrompt();
  const [expanded, setExpanded] = useState(false);

  const draws = useMemo(() => reportState.draws || [], [reportState.draws]);
  const contractor = (reportState.projectSummary as any)?.contractor as string | undefined;

  // ── Previous Applications ─────────────────────────────────────────────────

  const applications = useMemo(
    () => [...(reportState.recapApplications || [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [reportState.recapApplications],
  );

  /** One table per draw, in the order each first appears (the print order). */
  const applicationGroups = useMemo(() => {
    const groups = new Map<string, RecapApplicationDTO[]>();
    applications.forEach(application => {
      const group = groups.get(application.projectDrawId);
      if (group) group.push(application);
      else groups.set(application.projectDrawId, [application]);
    });
    return Array.from(groups.entries()).map(([projectDrawId, rows]) => ({ projectDrawId, rows }));
  }, [applications]);

  const drawsWithoutTable = useMemo(
    () => draws.filter(d => d.drawId && !applicationGroups.some(g => g.projectDrawId === d.drawId)),
    [draws, applicationGroups],
  );

  /** Writes the full list back in group order, renumbering displayOrder to match. */
  const commitApplications = useCallback(
    (next: RecapApplicationDTO[]) => {
      const renumbered = next.map((row, index) => ({ ...row, displayOrder: index }));
      setReportState((prev: MagicReportState) => ({ ...prev, recapApplications: renumbered }));
      setIsDirty(true);
    },
    [setReportState, setIsDirty],
  );

  const flattenGroups = (groups: { projectDrawId: string; rows: RecapApplicationDTO[] }[]) => groups.flatMap(g => g.rows);

  const updateApplication = useCallback(
    (rowId: string, key: string, value: any) => {
      const current = applications.find(a => a.id === rowId);
      if (!current) return;
      const normalized = key === 'applicationDate' ? (value ? `${value}T00:00:00` : null) : value;
      if ((current as any)[key] === normalized) return;
      commitApplications(applications.map(a => (a.id === rowId ? { ...a, [key]: normalized } : a)));
    },
    [applications, commitApplications],
  );

  const blankApplication = (projectDrawId: string, heading: string): RecapApplicationDTO => ({
    id: newId(),
    projectDrawId,
    heading,
    applicationNumber: '',
    applicationDate: null,
    requestedAmount: null,
    approvedAmount: null,
    retainage: null,
    percentRequested: null,
    percentComplete: null,
    contractAmount: null,
    displayOrder: 0,
  });

  const addApplication = (projectDrawId: string) => {
    const group = applicationGroups.find(g => g.projectDrawId === projectDrawId);
    const heading = group?.rows[0]?.heading || '';
    const groups = applicationGroups.map(g =>
      g.projectDrawId === projectDrawId ? { ...g, rows: [...g.rows, blankApplication(projectDrawId, heading)] } : g,
    );
    commitApplications(flattenGroups(groups));
  };

  const addApplicationTable = (projectDrawId: string) => {
    const draw = draws.find(d => d.drawId === projectDrawId);
    const heading = buildApplicationHeading(contractor, draw?.drawSummary?.name || '');
    commitApplications([...applications, blankApplication(projectDrawId, heading)]);
  };

  const deleteApplication = useCallback(
    async (rowId: string) => {
      const isConfirmed = await getConfirmation({
        heading: 'Delete application',
        description: 'Remove this application from the recap? This applies to this and all future reports.',
        confirmButton: { label: 'Delete', color: 'error' },
        cancelButton: { label: 'Cancel', color: 'secondary' },
      });
      if (!isConfirmed) return;
      commitApplications(applications.filter(a => a.id !== rowId));
    },
    [applications, commitApplications, getConfirmation],
  );

  // ── Previous Lien Waivers ─────────────────────────────────────────────────

  const lienWaiversSection: any = (reportState.sections as any)?.lienWaivers;
  const lienHistoryRaw: string | undefined = lienWaiversSection?.fields?.find((f: any) => f.fieldName === 'LienWaiverHistory')?.value;

  const visibleDraws = useMemo(
    () =>
      filterVisibleCoLienDraws(
        draws,
        resolveHiddenCoLienDrawIds(
          reportState.hiddenCoLienDrawIds,
          draws.map(d => d.drawId),
        ),
      ),
    [draws, reportState.hiddenCoLienDrawIds],
  );
  const visiblePrimaryDrawId = visibleDraws.length ? visibleDraws[visibleDraws.length - 1].drawId : undefined;

  // Rows carry no id; key them by position in the stored array (stable while the editor is open).
  const lienRows = useMemo(() => {
    const history = parseLienWaiverHistory(lienHistoryRaw).map((entry, index) => ({ ...entry, _key: String(index) }));
    return sortLienWaiverHistory(
      history,
      draws.map(d => d.drawId),
    ) as (LienWaiverHistoryEntry & { _key: string })[];
  }, [lienHistoryRaw, draws]);

  /** A covered draw's current-period row is owned by the Lien Waivers section. */
  const isCurrentPeriodLienRow = useCallback(
    (entry: LienWaiverHistoryEntry) => {
      const drawId = entry.drawId || visiblePrimaryDrawId;
      const draw = visibleDraws.find(d => d.drawId === drawId);
      return Boolean(draw) && String(draw?.drawSummary?.applicationNumber || '') === entry.periodKey;
    },
    [visibleDraws, visiblePrimaryDrawId],
  );

  const commitLienHistory = useCallback(
    (next: LienWaiverHistoryEntry[]) => {
      const value = JSON.stringify(next);
      setReportState((prev: MagicReportState) => {
        const sections: any = prev.sections || {};
        const section = sections.lienWaivers || { fields: [] };
        const fields: any[] = section.fields || [];
        const hasField = fields.some(f => f.fieldName === 'LienWaiverHistory');
        const nextFields = hasField
          ? fields.map(f => (f.fieldName === 'LienWaiverHistory' ? { ...f, value } : f))
          : [...fields, { fieldName: 'LienWaiverHistory', fieldType: FieldType.Text, value }];
        return { ...prev, sections: { ...sections, lienWaivers: { ...section, fields: nextFields } } };
      });
      setIsDirty(true);
    },
    [setReportState, setIsDirty],
  );

  const storedLienHistory = useCallback(() => parseLienWaiverHistory(lienHistoryRaw), [lienHistoryRaw]);

  const updateLienRow = useCallback(
    (key: string, field: string, value: any) => {
      const history = storedLienHistory();
      const index = Number(key);
      if (!history[index] || (history[index] as any)[field] === (value ?? '')) return;
      history[index] = { ...history[index], [field]: value ?? '', updatedAt: new Date().toISOString() };
      commitLienHistory(history);
    },
    [storedLienHistory, commitLienHistory],
  );

  const addLienRow = useCallback(() => {
    commitLienHistory([
      ...storedLienHistory(),
      {
        // A unique key keeps hand-added rows clear of the section's (drawId, application #) rows.
        periodKey: `manual-${newId()}`,
        drawId: visiblePrimaryDrawId,
        status: 'Conditional',
        isCumulative: false,
        throughCurrentPeriod: false,
        contractor: contractor || '',
        applicationDate: '',
        applicationNumber: '',
        amount: '',
        updatedAt: new Date().toISOString(),
      },
    ]);
  }, [storedLienHistory, commitLienHistory, visiblePrimaryDrawId, contractor]);

  const deleteLienRow = useCallback(
    async (key: string) => {
      const isConfirmed = await getConfirmation({
        heading: 'Delete lien waiver',
        description: 'Remove this lien waiver from the recap?',
        confirmButton: { label: 'Delete', color: 'error' },
        cancelButton: { label: 'Cancel', color: 'secondary' },
      });
      if (!isConfirmed) return;
      commitLienHistory(storedLienHistory().filter((_, index) => String(index) !== key));
    },
    [getConfirmation, storedLienHistory, commitLienHistory],
  );

  return (
    <Box id={RECAP_OF_PREVIOUS_REPORTS_SUBSTEP.id} sx={{ mb: 4 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{ px: 2, '&.MuiAccordion-root': { borderRadius: '10px' }, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
          <Typography variant='h5' color='primary' sx={{ lineHeight: 1.5 }}>
            Recap of Previous Reports
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pb: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary', display: 'block', mt: -1 }}>
            Filled from the uploaded report and updated every month. Changes made here carry over to all future reports.
          </Typography>

          <Box>
            <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5 }}>
              Previous Applications for Payment
            </Typography>
            {applicationGroups.length === 0 && (
              <Typography variant='body2' color='text.secondary'>
                No previous applications recorded.
              </Typography>
            )}
            <Stack spacing={3}>
              {applicationGroups.map(group => (
                <Box key={group.projectDrawId}>
                  <TableHeader
                    title={group.rows[0]?.heading || ''}
                    addTooltip='Add application'
                    onAdd={() => addApplication(group.projectDrawId)}
                  />
                  <RecapTable<RecapApplicationDTO>
                    rows={group.rows}
                    columns={APPLICATION_COLUMNS}
                    getRowId={row => row.id}
                    onCellChange={updateApplication}
                    onDeleteRow={deleteApplication}
                  />
                </Box>
              ))}
            </Stack>
            {drawsWithoutTable.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                {drawsWithoutTable.map(draw => (
                  <Button key={draw.drawId} variant='outlined' size='small' onClick={() => addApplicationTable(draw.drawId)}>
                    Add table for {draw.drawSummary?.name || 'draw'}
                  </Button>
                ))}
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5 }}>
              Previous Lien Waivers
            </Typography>
            <TableHeader title='Lien Waivers' addTooltip='Add lien waiver' onAdd={addLienRow} />
            <RecapTable<LienWaiverHistoryEntry & { _key: string }>
              rows={lienRows}
              columns={LIEN_COLUMNS}
              getRowId={row => row._key}
              onCellChange={updateLienRow}
              onDeleteRow={deleteLienRow}
              isRowLocked={isCurrentPeriodLienRow}
              lockedTooltip="This month's lien waiver comes from the Lien Waivers section. Edit it there."
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
